import { Typography, Card, Stack, useTheme, useMediaQuery } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
import PageHeader from '../../components/PageHeader';

const numberFormatter = new Intl.NumberFormat('he-IL', {
  maximumFractionDigits: 1,
});

const SummaryCard = ({ title, value, note, tone }) => (

  <Card
    sx={{
      minWidth: 0,
      p: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      boxShadow: 'var(--shadow-sm)',
      borderTopWidth: 4,
      borderTopColor:
        tone === 'metric-success'
          ? 'success.light'
          : tone === 'metric-warning'
            ? 'warning.light'
            : tone === 'metric-info'
              ? 'info.light'
              : 'error.light',
    }}
  >
    <Stack
      sx={{
        display: 'grid',
        gap: '4px',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontSize: 25,
        }}
      >
        {numberFormatter.format(value ?? 0)}
      </Typography>

      <Typography
        sx={{
          color: 'primary.main',
          fontSize: 11,
        }}
      >
        {note}
      </Typography>
    </Stack>
  </Card>
);

const BarChart = ({ title, items, dataKey, color, isMobile = false }) => {
  const total = items.reduce((sum, item) => sum + (item[dataKey] || 0), 0);
  const labels = items.map((item) => item.date?.slice(5).replace('-', '/') || '');
  const values = items.map((item) => item[dataKey] || 0);
  return (
    <Card
      sx={{
        minWidth: 0,
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <Stack justifyContent="space-between" alignItems="flex-start" mb={2}>

        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>

          <Typography sx={{ fontSize: 12 }} fontWeight={700}>
            {numberFormatter.format(total)}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          30 הימים האחרונים
        </Typography>
      </Stack>

      <MuiBarChart
        height={250}
        hideLegend
        slotProps={{
          tooltip: {
            trigger: 'item',
            sx: {
              direction: 'rtl',
              width: 'fit-content',
              minWidth: 180,
              maxWidth: 260,

              '& table': {
                width: 'auto',
                minWidth: 180,
                tableLayout: 'auto',
              },

              '& td': {
                whiteSpace: 'nowrap',
              },
            },
          },
        }}
        grid={{
          horizontal: true,
        }}
        xAxis={[
          {
            scaleType: 'band',
            data: labels,
            tickLabelInterval: (_, index) => index % 5 === 0 || index === labels.length - 1,
          },
        ]}
        yAxis={[
          {
            width: 42,
          },
        ]}
        series={[
          {
            data: values,
            label: title,
            color,
            valueFormatter: (value) => numberFormatter.format(value ?? 0),
          },
        ]}
        margin={{
          top: isMobile ? 0 : 10,
          right: isMobile ? 15 : 10,
          bottom: isMobile ? 0 : 30,
          left: isMobile ? 0 : 10,
        }}
        sx={{
          direction: 'ltr',
        }}
      />
    </Card>
  );
};

const GroupHeader = ({ title, description }) => (
  <Stack
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '16px',
    }}
  >
    <Stack>
      <Typography>{title}</Typography>
      <Typography>{description}</Typography>
    </Stack>
  </Stack>
);

const OverviewPage = ({ overview }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const metrics = overview.metrics || {};
  const daily = overview.daily || [];
  return (
    <Stack
      dir="rtl"
      sx={{
        display: 'grid',
        gap: '28px',
        paddingBottom: '24px',
      }}
    >
      <PageHeader title="סקירה" meta="תמונת מצב של פעילות המערכת" />

      <Stack
        sx={{
          display: 'grid',
          gap: '16px',
          paddingTop: '28px',
          borderTop: '1px solid var(--line)',
          '&:first-of-type': {
            paddingTop: '0',
            borderTop: '0',
          },
        }}
      >
        <GroupHeader title="מקומות" description="מצב מאגר המקומות והאימות" />

        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <SummaryCard
            title="כל המקומות"
            value={metrics.totalPlaces}
            note="רשומות במערכת"
          />

          <SummaryCard
            title="מקומות מאומתים"
            value={metrics.verifiedPlaces}
            note="מידע מנוהל מהמאגר"
            tone="metric-success"
          />
          <SummaryCard
            title="מקומות לא מאומתים"
            value={metrics.unverifiedPlaces}
            note="מידע מתקבל מ-Google"
            tone="metric-warning"
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          display: 'grid',
          gap: '16px',
          paddingTop: '28px',
          borderTop: '1px solid var(--line)',
          '&:first-of-type': {
            paddingTop: '0',
            borderTop: '0',
          },
        }}
      >
        <GroupHeader title="משתמשים" description="היקף המשתמשים והפעילות האחרונה" />

        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <SummaryCard
            title="סה״כ משתמשים"
            value={metrics.totalUsers}
            note="משתמשים רשומים במערכת"
          />
          <SummaryCard
            title="משתמשים פעילים"
            value={metrics.activeUsers}
            note="ייחודיים ב-24 השעות האחרונות"
            tone="metric-info"
          />
        </Stack>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <BarChart
            title="משתמשים פעילים"
            items={daily}
            dataKey="activeUsers"
            color="#2e90fa"
            isMobile={isMobile}
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          display: 'grid',
          gap: '16px',
          paddingTop: '28px',
          borderTop: '1px solid var(--line)',
          '&:first-of-type': {
            paddingTop: '0',
            borderTop: '0',
          },
        }}
      >
        <GroupHeader title="פעילות" description="חיפושים וניווטים ב־30 הימים האחרונים" />

        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <SummaryCard title="חיפושים היום" value={metrics.searchesToday} note="חיפושי מקומות" />
          <SummaryCard
            title="לחיצות בדרך לשם"
            value={metrics.navigationStartsToday}
            note="היום"
            tone="metric-info"
          />
        </Stack>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <BarChart
            title="חיפושים"
            items={daily}
            dataKey="searches"
            color="#0f766e"
            isMobile={isMobile}
          />
          <BarChart
            title="בדרך לשם"
            items={daily}
            dataKey="navigations"
            color="#2563eb"
            isMobile={isMobile}
          />
        </Stack>
      </Stack>

      <Stack
        sx={{
          display: 'grid',
          gap: '16px',
          paddingTop: '28px',
          borderTop: '1px solid var(--line)',
          '&:first-of-type': {
            paddingTop: '0',
            borderTop: '0',
          },
        }}
      >
        <GroupHeader title="דיווחים" description="דיווחי העומס לפי מקור" />

        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <SummaryCard
            title="דיווחי AI היום"
            value={metrics.aiReportsToday}
            note="הערכות שנוצרו אוטומטית"
          />
          <SummaryCard
            title="דיווחי GPS היום"
            value={metrics.gpsReportsToday}
            note="דיווחים מבוססי מיקום"
            tone="metric-info"
          />
          <SummaryCard
            title="דיווחי משתמש היום"
            value={metrics.userReportsToday}
            note="דיווחים שנשלחו מהאפליקציה"
            tone="metric-success"
          />
          <SummaryCard
            title="ממוצע דיווחים יומי"
            value={metrics.averageDailyReports}
            note="ממוצע ב-30 יום"
          />
        </Stack>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '16px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <BarChart
            title="דיווחי AI"
            items={daily}
            dataKey="aiReports"
            color="#b54708"
            isMobile={isMobile}
          />
          <BarChart
            title="דיווחי GPS"
            items={daily}
            dataKey="gpsReports"
            color="#2563eb"
            isMobile={isMobile}
          />
          <BarChart
            title="דיווחי משתמש"
            items={daily}
            dataKey="userReports"
            color="#12b76a"
            isMobile={isMobile}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
export default OverviewPage;
